import { REVIEW_CONFIDENCE_THRESHOLD, createTransaction } from '../transaction.js';
import { suggestCategory } from '../rules.js';
import type { Category, MerchantRule, PaymentSource, Transaction } from '../schemas.js';
import type { ParseOptions, ParseResult, ParsedReceipt } from './common.js';
import type { StatementRow } from './statement.js';
import { looksLikeMaybankReceipt, parseMaybankReceipt } from './maybank.js';
import { looksLikeTngReceipt, parseTngReceipt } from './tng.js';

export * from './common.js';
export { parseMaybankReceipt, looksLikeMaybankReceipt } from './maybank.js';
export { parseTngReceipt, looksLikeTngReceipt } from './tng.js';
export {
  parseStatementText,
  checkRunningBalance,
  reconcileWithBalance,
  type StatementRow,
  type StatementParseResult,
  type StatementParseOptions,
  type BalanceCheck,
} from './statement.js';

export type ReceiptParserId = 'maybank' | 'tng';

export interface DetectedParser {
  id: ReceiptParserId;
  label: string;
  confidence: number;
}

/** Work out which issuer a pasted receipt came from. */
export function detectParser(raw: string): DetectedParser | undefined {
  const isTng = looksLikeTngReceipt(raw);
  const isMaybank = looksLikeMaybankReceipt(raw);

  // TNG receipts mention DuitNow too, so the more specific signature wins.
  if (isTng) return { id: 'tng', label: "Touch 'n Go eWallet", confidence: isMaybank ? 0.7 : 0.95 };
  if (isMaybank) return { id: 'maybank', label: 'MAE / Maybank2u', confidence: 0.95 };
  return undefined;
}

/**
 * Parse receipt text, choosing the parser automatically unless one is forced.
 * When nothing is recognised, both parsers are tried and the more confident
 * result is kept -- still with a warning attached.
 */
export function parseReceiptText(
  raw: string,
  options: ParseOptions & { parser?: ReceiptParserId } = {},
): ParseResult {
  const forced = options.parser ?? detectParser(raw)?.id;

  if (forced === 'tng') return parseTngReceipt(raw, options);
  if (forced === 'maybank') return parseMaybankReceipt(raw, options);

  const attempts = [parseMaybankReceipt(raw, options), parseTngReceipt(raw, options)];
  const successes = attempts.filter(
    (result): result is { ok: true } & ParsedReceipt => result.ok === true,
  );
  if (successes.length === 0) {
    return {
      ok: false,
      parser: 'generic',
      reason:
        'This text was not recognised as a MAE, Maybank or Touch ’n Go receipt, and no amount could be read from it.',
    };
  }
  const best = successes.sort((a, b) => b.confidence - a.confidence)[0] as {
    ok: true;
  } & ParsedReceipt;
  return {
    ...best,
    // Unknown issuer: never let it through without a look.
    confidence: Math.min(best.confidence, 0.6),
    warnings: [...best.warnings, 'The issuer of this receipt could not be identified.'],
  };
}

export interface ReceiptToTransactionOptions {
  source: PaymentSource;
  accountLabel?: string;
  categories?: readonly Category[];
  rules?: readonly MerchantRule[];
  history?: readonly { merchant: string; categoryId: string; count: number }[];
  defaultCategoryId?: string;
  now?: Date;
  uuid?: () => string;
}

export interface ReceiptImportResult {
  transaction: Transaction;
  /** Fields the parser could not read; the review screen highlights these. */
  missingFields: string[];
  warnings: string[];
  reference?: string;
}

/**
 * Turn a successful parse into a transaction.
 *
 * A missing date falls back to "now", which is a real decision, not a guess at
 * the receipt's contents: it is recorded as a missing field, drops the
 * confidence, and forces the record into the review inbox.
 */
export function receiptToTransaction(
  parsed: ParsedReceipt,
  options: ReceiptToTransactionOptions,
): ReceiptImportResult {
  const now = options.now ?? new Date();
  const missingFields: string[] = [];

  if (!parsed.merchant) missingFields.push('merchant');
  if (!parsed.occurredAt) missingFields.push('date');
  if (!parsed.type) missingFields.push('type');

  const merchant = parsed.merchant?.value ?? '';
  const type = parsed.type?.value ?? 'expense';

  const suggestion = suggestCategory({
    merchant,
    type,
    rules: options.rules ?? [],
    history: options.history ?? [],
    ...(options.defaultCategoryId ? { defaultCategoryId: options.defaultCategoryId } : {}),
  });

  const confidence = Math.min(parsed.confidence, missingFields.length > 0 ? 0.65 : 1);

  const noteParts: string[] = [];
  if (parsed.reference) noteParts.push(`Ref ${parsed.reference.value}`);

  const transaction = createTransaction({
    type,
    amountSen: parsed.amountSen.value,
    currency: parsed.currency,
    merchant,
    merchantOriginal: merchant,
    categoryId: suggestion.categoryId,
    ...(options.accountLabel ? { accountLabel: options.accountLabel } : {}),
    source: options.source,
    occurredAt: parsed.occurredAt?.value ?? now.toISOString(),
    note: noteParts.join(' · ') || null,
    importConfidence: confidence,
    reviewStatus: confidence < REVIEW_CONFIDENCE_THRESHOLD ? 'needs-review' : 'confirmed',
    now,
    ...(options.uuid ? { uuid: options.uuid } : {}),
  });

  return {
    transaction,
    missingFields,
    warnings: parsed.warnings,
    ...(parsed.reference ? { reference: parsed.reference.value } : {}),
  };
}


export interface StatementImportOptions {
  accountLabel?: string;
  categories?: readonly Category[];
  rules?: readonly MerchantRule[];
  history?: readonly { merchant: string; categoryId: string; count: number }[];
  defaultCategoryId?: string;
  now?: Date;
  uuid?: () => string;
}

export interface StatementImportRow {
  row: StatementRow;
  transaction: Transaction;
}

/**
 * Turn parsed statement rows into transactions.
 *
 * The source is recorded as `csv-import` rather than a receipt source: this
 * came from a document the person exported, not from a single receipt, and the
 * History screen should say so.
 */
export function statementToTransactions(
  rows: readonly StatementRow[],
  options: StatementImportOptions = {},
): StatementImportRow[] {
  const now = options.now ?? new Date();

  return rows.map((row) => {
    const suggestion = suggestCategory({
      merchant: row.description,
      type: row.type,
      rules: options.rules ?? [],
      history: options.history ?? [],
      ...(options.defaultCategoryId ? { defaultCategoryId: options.defaultCategoryId } : {}),
    });

    // The row's own confidence caps the result: a row the parser was unsure
    // about cannot become certain just because its description matched a rule.
    const confidence = Math.min(row.confidence, Math.max(suggestion.confidence, 0.5));

    return {
      row,
      transaction: createTransaction({
        type: row.type,
        amountSen: row.amountSen,
        merchant: row.description,
        merchantOriginal: row.description,
        categoryId: suggestion.categoryId,
        ...(options.accountLabel ? { accountLabel: options.accountLabel } : {}),
        source: 'csv-import',
        occurredAt: row.occurredAt,
        importConfidence: Math.round(confidence * 100) / 100,
        reviewStatus: confidence < REVIEW_CONFIDENCE_THRESHOLD ? 'needs-review' : 'confirmed',
        now,
        ...(options.uuid ? { uuid: options.uuid } : {}),
      }),
    };
  });
}
